import { useState } from "react";
import { TrendingUp, ClipboardList, Mail, Menu, X, ChevronRight, LogOut } from "lucide-react";

/**
 * Mobile-only bottom tab bar. Renders 3 primary tabs + "More" drawer
 * containing all remaining accessible modules. Visible on screens < lg.
 */
export default function MobileBottomNav({
  activeTab,
  onTabChange,
  visibleItems,
  user,
  onLogout,
  badgeCounts = {},
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Primary tabs that are pinned in the bar (in this order, if accessible)
  const PRIMARY = [
    { id: "service-orders", label: "Tickets", icon: ClipboardList },
    { id: "financial", label: "Financial", icon: TrendingUp },
    { id: "webmail", label: "Webmail", icon: Mail },
  ];

  const accessibleIds = new Set(visibleItems.map((i) => i.id));
  const pinned = PRIMARY.filter((p) => accessibleIds.has(p.id));
  const pinnedIds = new Set(pinned.map((p) => p.id));
  const moreItems = visibleItems.filter((i) => !pinnedIds.has(i.id));

  const handleTab = (id) => {
    onTabChange(id);
    setMoreOpen(false);
  };

  return (
    <>
      {/* Bottom Tab Bar — visible only on mobile */}
      <nav
        data-testid="mobile-bottom-nav"
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-forest-700 border-t border-forest-600 shadow-[0_-4px_12px_rgba(0,0,0,0.15)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-stretch justify-around">
          {pinned.map((p) => {
            const Icon = p.icon;
            const isActive = activeTab === p.id;
            const badge = badgeCounts[p.id === "service-orders" ? "soApprovals" : p.id === "leads" ? "leads" : ""] || 0;
            return (
              <button
                key={p.id}
                data-testid={`mobile-nav-${p.id}`}
                onClick={() => handleTab(p.id)}
                className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] transition-colors relative ${
                  isActive ? "text-white" : "text-forest-300 active:text-white"
                }`}
              >
                {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-forest-200 rounded-full" />}
                <div className="relative">
                  <Icon className="w-5 h-5" />
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-red-500 text-white">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium leading-none">{p.label}</span>
              </button>
            );
          })}
          {/* More button */}
          <button
            data-testid="mobile-nav-more"
            onClick={() => setMoreOpen(true)}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] transition-colors relative ${
              moreOpen ? "text-white" : "text-forest-300 active:text-white"
            }`}
          >
            <div className="relative">
              <Menu className="w-5 h-5" />
              {/* Leads badge surfaces on More if leads isn't pinned */}
              {!pinnedIds.has("leads") && (badgeCounts.leads || 0) > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-[9px] font-bold bg-red-500 text-white">
                  {badgeCounts.leads > 99 ? "99+" : badgeCounts.leads}
                </span>
              )}
            </div>
            <span className="text-[10px] font-medium leading-none">More</span>
          </button>
        </div>
      </nav>

      {/* More drawer (slides from right) */}
      {moreOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setMoreOpen(false)} />
          <aside
            data-testid="mobile-more-drawer"
            className="lg:hidden fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
          >
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between bg-forest-700 text-white">
              <div>
                <p className="text-sm font-semibold">{user?.name || "Admin"}</p>
                <p className="text-xs text-forest-200">{user?.role || "admin"}</p>
              </div>
              <button onClick={() => setMoreOpen(false)} className="text-forest-200 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {moreItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                const badge = item.badgeKey ? (badgeCounts[item.badgeKey] || 0) : 0;
                return (
                  <button
                    key={item.id}
                    data-testid={`mobile-more-${item.id}`}
                    onClick={() => handleTab(item.id)}
                    className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm font-medium transition-colors min-h-[48px] ${
                      isActive ? "bg-forest-50 text-forest-800 border-r-[3px] border-forest-600" : "text-stone-700 hover:bg-stone-50 active:bg-stone-100"
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {badge > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold bg-red-500 text-white">
                        {badge > 99 ? "99+" : badge}
                      </span>
                    )}
                    {isActive && <ChevronRight className="w-4 h-4 text-forest-500" />}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-stone-200 p-3">
              <button
                data-testid="mobile-logout-btn"
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 active:bg-red-100 min-h-[44px]"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
